import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import TourForm from '../../components/admin/TourForm';
import { Link } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { Tour } from '../../types/tours';
import { supabase } from '../../lib/supabase';

const EditTour = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        // Fetch tour data
        const { data: tourData, error: tourError } = await supabase
          .from('tours')
          .select('*')
          .eq('id', id)
          .single();

        if (tourError) throw tourError;
        if (!tourData) throw new Error('Tour not found');

        // Fetch tour images
        const { data: imageData, error: imageError } = await supabase
          .from('tour_images')
          .select('*')
          .eq('tour_id', id)
          .eq('tour_id', id)
          .order('display_order');

        if (imageError) throw imageError;

        // Fetch tour items
        const { data: itemsData, error: itemsError } = await supabase
          .from('tour_shared_items')
          .select('*, item:shared_items(*)')
          .eq('tour_id', id)
          .order('display_order');

        if (itemsError) throw itemsError;

        // Organize images by type
        const carouselImages = imageData
          .filter(img => img.image_type === 'carousel')
          .map(img => img.image_url);

        const galleryImages = imageData
          .filter(img => img.image_type === 'gallery')
          .map(img => img.image_url);

        setTour({
          ...tourData,
          carousel_images: carouselImages,
          gallery_images: galleryImages,
          include_items: itemsData
            .filter(item => item.item.type === 'include')
            .map(item => item.item_id),
          bring_items: itemsData
            .filter(item => item.item.type === 'bring')
            .map(item => item.item_id)
        });
      } catch (err) {
        console.error('Error fetching tour:', err);
        setError('Failed to fetch tour');
        navigate('/kh-admin');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTour();
    }
  }, [id, navigate]);

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">{error}</div>;
  if (!tour) return null;

  return (
    <AdminLayout>
      <div className="p-8">
        {/* Breadcrumbs */}
        <div className="flex items-center gap-2 mb-8 text-sm">
          <Link to="/kh-admin" className="text-white/70 hover:text-white transition-colors">
            <Home className="w-4 h-4" />
          </Link>
          <ChevronRight className="w-4 h-4 text-white/40" />
          <Link to="/kh-admin/tours" className="text-white/70 hover:text-white transition-colors">
            Tours
          </Link>
          <ChevronRight className="w-4 h-4 text-white/40" />
          <span className="text-white">Edit Tour</span>
        </div>

        <h1 className="text-3xl font-bold mb-8">Edit Tour</h1>
        <TourForm mode="edit" initialData={tour} />
      </div>
    </AdminLayout>
  );
};

export default EditTour;